import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import UndoIcon from '@mui/icons-material/Undo';
import moment from 'moment';
import { appConstants } from '../_constants/app.constants';
import { useConfirm } from '../common/ConfirmDialog';
import {
  bracketMatchHasBothTeams,
  bracketSideLabel,
  bracketSideShield,
  buildBracketMatchUpdatePayload,
  buildLeagueMatchUpdatePayload,
} from './mesaUtils';
import MatchTimer, { markMatchStarted } from './MatchTimer';

const API = appConstants.API_URL;

const clampGoals = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(99, Math.trunc(x)));
};

/**
 * Tarjeta de marcador de un partido. Autónoma: gestiona su propio estado y guarda
 * en el servidor. Sirve tanto para partidos de liga (kind="league") como de
 * eliminatoria (kind="bracket"). Pensada para apilar varias en la mesa múltiple.
 */
export default function MesaMatchCard({ match, kind, onChanged, highlighted = false }) {
  const confirm = useConfirm();
  const isBracket = kind === 'bracket';

  const [m, setM] = useState(match);
  const [lg, setLg] = useState(clampGoals(match.localGoals ?? 0));
  const [fg, setFg] = useState(clampGoals(match.foreignGoals ?? 0));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const lockRef = useRef(false);

  const pending = !m.isActive && !m.completed;
  const live = m.isActive && !m.completed;
  const done = Boolean(m.completed);

  const teamsResolved = isBracket ? bracketMatchHasBothTeams(m) : true;
  const canEditGoals = live && teamsResolved;

  const localName = isBracket ? bracketSideLabel(m, 'local') : m.localTeam?.name || 'Local';
  const foreignName = isBracket ? bracketSideLabel(m, 'foreign') : m.foreignTeam?.name || 'Visitante';
  const localShield = isBracket ? bracketSideShield(m, 'local') : m.localTeam?.shield;
  const foreignShield = isBracket ? bracketSideShield(m, 'foreign') : m.foreignTeam?.shield;

  const statusChip = useMemo(() => {
    if (done) return { label: 'Finalizado', color: 'default' };
    if (live) return { label: 'En juego', color: 'success' };
    return { label: 'Pendiente', color: 'warning' };
  }, [done, live]);

  const applyResponse = useCallback(
    (raw, base) => {
      const merged = {
        ...base,
        ...raw,
        localTeam: raw.localTeam ?? base.localTeam,
        foreignTeam: raw.foreignTeam ?? base.foreignTeam,
        groupPositionLocal: raw.groupPositionLocal ?? base.groupPositionLocal,
        groupPositionForeign: raw.groupPositionForeign ?? base.groupPositionForeign,
      };
      setM(merged);
      setLg(clampGoals(merged.localGoals ?? 0));
      setFg(clampGoals(merged.foreignGoals ?? 0));
      if (onChanged) onChanged(merged);
      return merged;
    },
    [onChanged],
  );

  const save = useCallback(
    async ({ localGoals, foreignGoals, isActive, completed }) => {
      if (lockRef.current) return;
      lockRef.current = true;
      setSaving(true);
      setErr('');
      try {
        const body = isBracket
          ? buildBracketMatchUpdatePayload(m, { localGoals, foreignGoals, isActive, completed })
          : buildLeagueMatchUpdatePayload(m, { localGoals, foreignGoals, isActive, completed });
        const url = isBracket
          ? `${API}/api/tournamentmatches/${m.id}/finalmatch`
          : `${API}/api/tournamentmatches/${m.id}`;
        const res = await axios.put(url, body);
        applyResponse(res.data ?? {}, m);
      } catch (e) {
        setErr(e.response?.data?.message || e.message || 'No se pudo guardar');
        // Revertir el marcador visible al último valor confirmado.
        setLg(clampGoals(m.localGoals ?? 0));
        setFg(clampGoals(m.foreignGoals ?? 0));
      } finally {
        lockRef.current = false;
        setSaving(false);
      }
    },
    [m, isBracket, applyResponse],
  );

  const bump = useCallback(
    (side, delta) => {
      if (!canEditGoals || lockRef.current) return;
      const nextLocal = side === 'local' ? clampGoals(lg + delta) : lg;
      const nextForeign = side === 'foreign' ? clampGoals(fg + delta) : fg;
      if (nextLocal === lg && nextForeign === fg) return;
      setLg(nextLocal);
      setFg(nextForeign);
      void save({ localGoals: nextLocal, foreignGoals: nextForeign, isActive: true, completed: false });
    },
    [canEditGoals, lg, fg, save],
  );

  const commitManual = useCallback(
    (side, rawValue) => {
      if (!canEditGoals || lockRef.current) return;
      const value = clampGoals(rawValue);
      const nextLocal = side === 'local' ? value : lg;
      const nextForeign = side === 'foreign' ? value : fg;
      if (nextLocal === clampGoals(m.localGoals ?? 0) && nextForeign === clampGoals(m.foreignGoals ?? 0)) {
        setLg(nextLocal);
        setFg(nextForeign);
        return;
      }
      setLg(nextLocal);
      setFg(nextForeign);
      void save({ localGoals: nextLocal, foreignGoals: nextForeign, isActive: true, completed: false });
    },
    [canEditGoals, lg, fg, m.localGoals, m.foreignGoals, save],
  );

  const handleStart = useCallback(() => {
    markMatchStarted(m.id);
    void save({ localGoals: lg, foreignGoals: fg, isActive: true, completed: false });
  }, [save, lg, fg, m.id]);

  const handleFinish = useCallback(async () => {
    const ok = await confirm({
      title: 'Finalizar partido',
      message: `${localName} ${lg} — ${fg} ${foreignName}. El partido se cerrará con este resultado.`,
      confirmText: 'Finalizar',
    });
    if (!ok) return;
    void save({ localGoals: lg, foreignGoals: fg, isActive: false, completed: true });
  }, [confirm, save, lg, fg, localName, foreignName]);

  const handleReopen = useCallback(() => {
    void save({ localGoals: lg, foreignGoals: fg, isActive: true, completed: false });
  }, [save, lg, fg]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: highlighted ? '2px solid #0f6b5c' : '1px solid rgba(15,53,49,0.14)',
        bgcolor: highlighted ? 'rgba(15,107,92,0.05)' : '#fff',
        opacity: saving ? 0.78 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {m.fieldName ? `${m.fieldName} · ` : ''}
            {moment(m.dayHour).format('ddd D MMM, HH:mm')}
          </Typography>
          {isBracket && m.name ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#8b6914', lineHeight: 1.2 }}>
              {m.name}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" alignItems="center" gap={0.75}>
          {live ? <MatchTimer matchId={m.id} running={live} sx={{ fontSize: '0.95rem' }} /> : null}
          {highlighted ? (
            <Chip size="small" label="Siguiente" sx={{ fontWeight: 800, bgcolor: '#0f6b5c', color: '#fff' }} />
          ) : null}
          {saving ? <CircularProgress size={16} thickness={6} sx={{ color: '#0f6b5c' }} /> : null}
          <Chip size="small" label={statusChip.label} color={statusChip.color} sx={{ fontWeight: 700 }} />
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <MatchSide
          name={localName}
          shield={localShield}
          value={lg}
          onBump={(d) => bump('local', d)}
          onCommit={(v) => commitManual('local', v)}
          disabled={!canEditGoals || saving}
        />
        <Typography sx={{ fontWeight: 800, color: 'text.secondary', alignSelf: 'center', pt: 2 }}>vs</Typography>
        <MatchSide
          name={foreignName}
          shield={foreignShield}
          value={fg}
          onBump={(d) => bump('foreign', d)}
          onCommit={(v) => commitManual('foreign', v)}
          disabled={!canEditGoals || saving}
        />
      </Stack>

      {isBracket && !teamsResolved ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Equipos pendientes: no se puede marcar hasta que ambos bandos tengan equipo.
        </Typography>
      ) : null}

      {err ? (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1, fontWeight: 600 }}>
          {err}
        </Typography>
      ) : null}

      <Box sx={{ mt: 1.25 }}>
        {pending ? (
          <Button
            fullWidth
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={saving || (isBracket && !teamsResolved)}
            onClick={handleStart}
            sx={{ fontWeight: 800, textTransform: 'none', bgcolor: '#0f6b5c', '&:hover': { bgcolor: '#0d5a4f' } }}
          >
            Empezar partido
          </Button>
        ) : null}
        {live ? (
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            disabled={saving}
            onClick={handleFinish}
            sx={{ fontWeight: 800, textTransform: 'none' }}
          >
            Finalizar partido
          </Button>
        ) : null}
        {done ? (
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            disabled={saving}
            onClick={handleReopen}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            Reabrir para corregir
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}

function MatchSide({ name, shield, value, onBump, onCommit, disabled }) {
  const [draft, setDraft] = useState(String(value));

  // Mantiene el input sincronizado cuando el valor cambia desde fuera (+/- o servidor).
  React.useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '' || Number.isNaN(Number(trimmed))) {
      setDraft(String(value));
      return;
    }
    onCommit(trimmed);
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.5, justifyContent: 'center' }}>
        <Avatar src={shield || undefined} variant="rounded" sx={{ width: 26, height: 26 }} />
        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap title={name}>
          {name}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
        <Button
          variant="outlined"
          disabled={disabled || value <= 0}
          onClick={() => onBump(-1)}
          sx={{ minWidth: 44, minHeight: 48, fontWeight: 800, fontSize: '1.2rem', borderRadius: 1.5, p: 0 }}
        >
          −
        </Button>
        <input
          type="number"
          inputMode="numeric"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          aria-label={`Goles de ${name}`}
          style={{
            width: 48,
            height: 48,
            textAlign: 'center',
            fontSize: '1.7rem',
            fontWeight: 900,
            color: '#0f6b5c',
            border: '1px solid rgba(15,53,49,0.18)',
            borderRadius: 8,
            background: disabled ? '#f4f4f0' : '#fff',
            MozAppearance: 'textfield',
          }}
        />
        <Button
          variant="contained"
          disabled={disabled || value >= 99}
          onClick={() => onBump(1)}
          sx={{
            minWidth: 44,
            minHeight: 48,
            fontWeight: 800,
            fontSize: '1.2rem',
            borderRadius: 1.5,
            p: 0,
            bgcolor: '#0f6b5c',
            '&:hover': { bgcolor: '#0d5a4f' },
          }}
        >
          +
        </Button>
      </Stack>
    </Box>
  );
}
